import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/crmApi";
import { PageHeader, Loader } from "@/crm/ui";
import { toast } from "sonner";
import {
  Users, Trophy, TrendingUp, TrendingDown, Minus, Bot, Target, RefreshCw,
  BarChart3, Award, AlertTriangle, X, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, Legend, Cell,
} from "recharts";
import Modal from "@/crm/components/Modal";

const COLORS = ["#4f46e5", "#059669", "#f59e0b", "#e11d48", "#0ea5e9", "#a855f7", "#14b8a6", "#f97316"];
const SCORE_DIMS = [
  ["activite", "Activité"], ["efficacite", "Efficacité"], ["conversion", "Conversion"],
  ["qualite_crm", "Qualité CRM"], ["regularite", "Régularité"], ["suivi", "Suivi"], ["global", "Score global"],
];
const KPI_OPTIONS = [
  { key: "global", label: "Score global", path: (s) => s.scores.global },
  { key: "appels", label: "Appels", path: (s) => s.volume.appels },
  { key: "rdv", label: "Rendez-vous", path: (s) => s.volume.rdv },
  { key: "clients", label: "Clients", path: (s) => s.portfolio.clients },
  { key: "taux_conversion", label: "Taux de conversion", path: (s) => s.rates.taux_conversion },
  { key: "qualite_crm", label: "Qualité CRM", path: (s) => s.scores.qualite_crm },
];
const WD = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function scoreColor(v) {
  if (v >= 85) return "text-emerald-600";
  if (v >= 70) return "text-sky-600";
  if (v >= 50) return "text-amber-600";
  return "text-rose-600";
}

export default function Comparison() {
  const [meta, setMeta] = useState(null);
  const [selected, setSelected] = useState([]);
  const [period, setPeriod] = useState("this_month");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [sector, setSector] = useState("");
  const [data, setData] = useState(null);
  const [evolution, setEvolution] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("global");
  const [kpi, setKpi] = useState("global");
  const [analysis, setAnalysis] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [showObjectives, setShowObjectives] = useState(false);

  useEffect(() => {
    api.get("/comparison/meta").then(({ data }) => {
      setMeta(data);
      setSelected(data.commercials.map((c) => c.id));
    }).catch(() => setMeta({ commercials: [], campaigns: [], sectors: [], periods: [] }));
  }, []);

  const fetchData = useCallback(async () => {
    if (!selected.length) { setData(null); return; }
    setLoading(true);
    const params = new URLSearchParams({ ids: selected.join(","), period });
    if (period === "custom") { params.append("start", customStart); params.append("end", customEnd); }
    if (campaignId) params.append("campaign_id", campaignId);
    if (sector) params.append("sector", sector);
    try {
      const [{ data: cmp }, { data: evo }] = await Promise.all([
        api.get(`/comparison?${params}`),
        api.get(`/comparison/evolution?ids=${selected.join(",")}&weeks=4`),
      ]);
      setData(cmp); setEvolution(evo);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [selected, period, customStart, customEnd, campaignId, sector]);

  useEffect(() => {
    if (period === "custom" && (!customStart || !customEnd)) return;
    const t = setTimeout(fetchData, 200);
    return () => clearTimeout(t);
  }, [fetchData, period, customStart, customEnd]);

  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const selectAll = () => setSelected(meta.commercials.map((c) => c.id));
  const clearAll = () => setSelected([]);

  const runAnalysis = async () => {
    setAnalyzing(true); setAnalysis("");
    try {
      const body = { ids: selected.join(","), period, campaign_id: campaignId, sector };
      if (period === "custom") { body.start = customStart; body.end = customEnd; }
      const { data } = await api.post("/comparison/analyze", body);
      setAnalysis(data.analysis);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  if (!meta) return <Loader />;

  const rows = data ? [...data.commercials] : [];
  rows.sort((a, b) => {
    const dim = ["appels", "emails", "relances", "rdv"].includes(sortBy) ? b.volume[sortBy] - a.volume[sortBy]
      : sortBy === "clients" ? b.portfolio.clients - a.portfolio.clients
      : sortBy === "taux_conversion" ? b.rates.taux_conversion - a.rates.taux_conversion
      : (b.scores[sortBy] ?? 0) - (a.scores[sortBy] ?? 0);
    return dim;
  });
  const colorFor = (id) => COLORS[data.commercials.findIndex((c) => c.id === id) % COLORS.length];
  const kpiOpt = KPI_OPTIONS.find((k) => k.key === kpi);
  const chartData = data ? data.commercials.map((s) => ({ name: s.name, value: kpiOpt.path(s), id: s.id })) : [];

  return (
    <div>
      <PageHeader title="Comparaison des commerciaux" subtitle="Volume · Efficacité · Qualité · Conversion · Régularité"
        actions={
          <div className="flex gap-2">
            <button data-testid="objectives-btn" onClick={() => setShowObjectives(true)} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50"><Target size={15} /> Objectifs</button>
            <button data-testid="ai-analyze-btn" onClick={runAnalysis} disabled={analyzing || !selected.length} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50">
              {analyzing ? <RefreshCw size={15} className="animate-spin" /> : <Bot size={15} />} Analyser (IA)
            </button>
          </div>
        } />

      {/* Contrôles */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-slate-500">Période</label>
            <select data-testid="period-select" value={period} onChange={(e) => setPeriod(e.target.value)} className="block mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white">
              {meta.periods.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </div>
          {period === "custom" && (
            <>
              <div><label className="text-xs text-slate-500">Du</label><input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="block mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" /></div>
              <div><label className="text-xs text-slate-500">Au</label><input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="block mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm" /></div>
            </>
          )}
          <div>
            <label className="text-xs text-slate-500">Campagne</label>
            <select data-testid="campaign-filter" value={campaignId} onChange={(e) => setCampaignId(e.target.value)} className="block mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white max-w-48">
              <option value="">Toutes</option>
              {meta.campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Secteur</label>
            <select data-testid="sector-filter" value={sector} onChange={(e) => setSector(e.target.value)} className="block mt-1 px-3 py-2 rounded-lg border border-slate-300 text-sm bg-white max-w-48">
              <option value="">Tous</option>
              {meta.sectors.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1"><label className="text-xs text-slate-500">Commerciaux</label>
            <button onClick={selectAll} className="text-xs text-indigo-600">Tous</button>
            <button onClick={clearAll} className="text-xs text-slate-400">Aucun</button>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="commercial-selector">
            {meta.commercials.map((c) => (
              <button key={c.id} data-testid={`select-${c.id}`} onClick={() => toggle(c.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm border ${selected.includes(c.id) ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600"}`}>
                <span className="h-2 w-2 rounded-full" style={{ background: COLORS[meta.commercials.indexOf(c) % COLORS.length] }} />
                {c.name}{c.role === "manager" ? " (mgr)" : ""}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && !data ? <Loader /> : !data ? (
        <div className="bg-white rounded-xl border border-slate-200 p-10 text-center text-slate-400 text-sm">Sélectionnez au moins un commercial.</div>
      ) : (
        <div className="space-y-4">
          {analysis && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4" data-testid="ai-analysis">
              <div className="flex items-center gap-2 font-heading font-semibold text-indigo-700 mb-2"><Sparkles size={16} /> Analyse IA</div>
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{analysis}</p>
            </div>
          )}

          {/* Points forts / faibles */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-heading font-semibold text-slate-800 mb-3 flex items-center gap-2"><Award size={16} className="text-emerald-600" /> Points forts</h3>
              <div className="space-y-2">
                {data.highlights.strengths.map((h, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm" data-testid="strength-item">
                    <span className="text-lg">{["🥇", "🥈", "🥉"][i] || "•"}</span>
                    <strong>{h.name}</strong><span className="text-slate-500">— {h.label} ({h.value})</span>
                  </div>
                ))}
                {!data.highlights.strengths.length && <p className="text-sm text-slate-400">—</p>}
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-heading font-semibold text-slate-800 mb-3 flex items-center gap-2"><AlertTriangle size={16} className="text-amber-500" /> Points à surveiller</h3>
              <div className="space-y-2">
                {data.highlights.weaknesses.map((w, i) => (
                  <div key={i} className="text-sm" data-testid="weakness-item">
                    <strong>{w.name}</strong>
                    <ul className="list-disc pl-5 text-slate-500">{w.warnings.map((x, j) => <li key={j}>{x}</li>)}</ul>
                  </div>
                ))}
                {!data.highlights.weaknesses.length && <p className="text-sm text-slate-400">Aucun point critique détecté</p>}
              </div>
            </div>
          </div>

          {/* Scores globaux */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><Trophy size={16} /> Scores ({data.period.label})</h3>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Classer par</span>
                <select data-testid="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="px-2 py-1 rounded-lg border border-slate-300 text-sm bg-white">
                  {SCORE_DIMS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  <option value="appels">Appels</option><option value="rdv">RDV</option>
                  <option value="clients">Clients</option><option value="taux_conversion">Taux conversion</option>
                </select>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="scores-table">
                <thead><tr className="text-xs text-slate-500 bg-slate-50 border-b border-slate-200 text-left">
                  <th className="px-4 py-2">Commercial</th>
                  {SCORE_DIMS.map(([k, l]) => <th key={k} className="px-3 py-2 text-center">{l}</th>)}
                </tr></thead>
                <tbody>
                  {rows.map((s, idx) => (
                    <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`score-row-${s.id}`}>
                      <td className="px-4 py-2 font-medium text-slate-800 flex items-center gap-2">
                        {idx === 0 && <Trophy size={13} className="text-amber-500" />}
                        <span className="h-2 w-2 rounded-full" style={{ background: colorFor(s.id) }} />{s.name}
                      </td>
                      {SCORE_DIMS.map(([k]) => (
                        <td key={k} className={`px-3 py-2 text-center font-mono font-semibold ${scoreColor(s.scores[k])} ${k === "global" ? "text-base" : ""}`}>{s.scores[k]}</td>
                      ))}
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-medium text-slate-500">
                    <td className="px-4 py-2">Moyenne équipe</td>
                    {SCORE_DIMS.map(([k]) => <td key={k} className="px-3 py-2 text-center font-mono">{data.team_avg.scores?.[k]}</td>)}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Graphique KPI */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-slate-800 flex items-center gap-2"><BarChart3 size={16} /> Comparaison visuelle</h3>
              <select data-testid="kpi-select" value={kpi} onChange={(e) => setKpi(e.target.value)} className="px-2 py-1 rounded-lg border border-slate-300 text-sm bg-white">
                {KPI_OPTIONS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
              </select>
            </div>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" name={kpiOpt.label} radius={[4, 4, 0, 0]}>
                  {chartData.map((d) => <Cell key={d.id} fill={colorFor(d.id)} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Évolution */}
          {evolution?.series?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-heading font-semibold text-slate-800 mb-4 flex items-center gap-2"><TrendingUp size={16} /> Évolution (4 dernières semaines)</h3>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                  <XAxis dataKey="label" type="category" allowDuplicatedCategory={false} tick={{ fontSize: 11 }}
                    data={evolution.series[0].points} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} /><Tooltip /><Legend />
                  {evolution.series.map((s) => (
                    <Line key={s.id} data={s.points} dataKey="score" name={s.name} stroke={colorFor(s.id)} strokeWidth={2} dot={{ r: 3 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
              <p className="text-xs text-slate-400 mt-2">Indice hebdomadaire = appels + RDV×3 + clients×10</p>
            </div>
          )}

          {/* Portefeuille */}
          <CompareTable title="Portefeuille" testid="portfolio-table" rows={rows}
            cols={[["portefeuille", "Portefeuille"], ["traites", "Traités"], ["non_contactes", "Non traités"], ["interesses", "Intéressés"], ["rdv", "RDV"], ["a_rappeler", "À rappeler"], ["en_retard", "En retard"], ["clients", "Clients"], ["perdus", "Perdus"]]}
            get={(s, k) => s.portfolio[k]} avg={data.team_avg} colorFor={colorFor} />

          {/* Activité */}
          <CompareTable title="Activité" testid="activity-table" rows={rows}
            cols={[["appels", "Appels"], ["appels_aboutis", "Aboutis"], ["appels_sans_reponse", "Sans réponse"], ["emails", "Emails"], ["relances", "Relances"], ["rdv", "RDV"], ["taches_terminees", "Tâches OK"], ["taches_en_retard", "Tâches retard"]]}
            get={(s, k) => s.volume[k]} colorFor={colorFor} />

          {/* Efficacité */}
          <CompareTable title="Efficacité (ratios)" testid="efficiency-table" rows={rows}
            cols={[["taux_contact", "Taux contact"], ["taux_interet", "Taux intérêt"], ["taux_rdv", "Taux RDV"], ["taux_conversion", "Taux conversion"], ["taux_transformation", "Taux transformation"]]}
            get={(s, k) => `${s.rates[k]}%`} colorFor={colorFor} />

          {/* Tunnel de conversion */}
          <CompareTable title="Tunnel de conversion" testid="funnel-table" rows={rows}
            cols={[["prospects", "Prospects"], ["contactes", "Contactés"], ["interesses", "Intéressés"], ["rdv", "RDV"], ["propositions", "Propositions"], ["clients", "Clients"]]}
            get={(s, k) => s.funnel[k]} colorFor={colorFor} />

          {/* Objectifs */}
          {rows.some((s) => s.objectives) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5">
              <h3 className="font-heading font-semibold text-slate-800 mb-3 flex items-center gap-2"><Target size={16} /> Objectifs vs réalisé</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="objectives-table">
                  <thead><tr className="text-xs text-slate-500 bg-slate-50 border-b border-slate-200 text-left"><th className="px-4 py-2">Commercial</th>{["Appels", "Emails", "RDV", "Clients"].map((h) => <th key={h} className="px-3 py-2 text-center">{h}</th>)}</tr></thead>
                  <tbody>
                    {rows.map((s) => (
                      <tr key={s.id} className="border-b border-slate-50">
                        <td className="px-4 py-2 font-medium text-slate-800">{s.name}</td>
                        {["appels", "emails", "rdv", "clients"].map((k) => {
                          const o = s.objectives?.[k];
                          return <td key={k} className="px-3 py-2 text-center">
                            {o && o.objectif > 0 ? <span className="font-mono text-xs">{o.realise}/{o.objectif} {o.avancement != null && <b className={scoreColor(o.avancement)}>({o.avancement}%)</b>}</span> : <span className="text-slate-300">—</span>}
                          </td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Régularité par jour */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-heading font-semibold text-slate-800 mb-3 flex items-center gap-2"><BarChart3 size={16} /> Régularité (activité par jour)</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="regularity-table">
                <thead><tr className="text-xs text-slate-500 bg-slate-50 border-b border-slate-200 text-left"><th className="px-4 py-2">Commercial</th>{WD.map((d) => <th key={d} className="px-3 py-2 text-center">{d}</th>)}<th className="px-3 py-2 text-center">Score</th></tr></thead>
                <tbody>
                  {rows.map((s) => {
                    const mx = Math.max(...s.weekday, 1);
                    return <tr key={s.id} className="border-b border-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-800">{s.name}</td>
                      {s.weekday.map((n, i) => (
                        <td key={i} className="px-3 py-2 text-center">
                          <span className="inline-block rounded px-2 py-0.5 text-xs font-mono" style={{ background: `rgba(79,70,229,${0.12 + 0.6 * (n / mx)})`, color: n > mx * 0.5 ? "#fff" : "#3730a3" }}>{n}</span>
                        </td>
                      ))}
                      <td className={`px-3 py-2 text-center font-mono font-semibold ${scoreColor(s.scores.regularite)}`}>{s.scores.regularite}</td>
                    </tr>;
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showObjectives && <ObjectivesModal commercials={meta.commercials} onClose={() => setShowObjectives(false)} onSaved={() => { setShowObjectives(false); fetchData(); }} />}
    </div>
  );
}

function CompareTable({ title, testid, rows, cols, get, colorFor }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <h3 className="font-heading font-semibold text-slate-800 p-4 border-b border-slate-100">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid={testid}>
          <thead><tr className="text-xs text-slate-500 bg-slate-50 border-b border-slate-200 text-left">
            <th className="px-4 py-2">Commercial</th>{cols.map(([k, l]) => <th key={k} className="px-3 py-2 text-center">{l}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-4 py-2 font-medium text-slate-800 flex items-center gap-2"><span className="h-2 w-2 rounded-full" style={{ background: colorFor(s.id) }} />{s.name}</td>
                {cols.map(([k]) => <td key={k} className="px-3 py-2 text-center font-mono text-slate-700">{get(s, k)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ObjectivesModal({ commercials, onClose, onSaved }) {
  const [rows, setRows] = useState({});
  const [edited, setEdited] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/comparison/objectives").then(({ data }) => {
      const map = {};
      data.forEach((o) => { map[o.user_id] = o; });
      setRows(map); setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);
  const setVal = (uid, key, val) => {
    setRows((r) => ({ ...r, [uid]: { ...r[uid], [key]: val } }));
    setEdited((e) => ({ ...e, [uid]: true }));
  };
  const saveAll = async () => {
    const uids = Object.keys(edited);
    if (!uids.length) { toast.info("Aucune modification"); return; }
    setSaving(true);
    try {
      await Promise.all(uids.map((uid) => api.put(`/comparison/objectives/${uid}`, {
        appels: rows[uid]?.appels || 0, emails: rows[uid]?.emails || 0,
        rdv: rows[uid]?.rdv || 0, clients: rows[uid]?.clients || 0,
      })));
      toast.success("Objectifs enregistrés"); onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Modal onClose={onClose} size="2xl" testid="objectives-modal" label="Objectifs mensuels">
      <div className="flex items-center justify-between gap-3 mb-4"><h3 className="font-heading font-bold text-lg">Objectifs mensuels</h3><button onClick={onClose} aria-label="Fermer" className="text-slate-400 hover:text-slate-900"><X size={18} /></button></div>
      {!loaded ? <Loader label="Chargement des objectifs…" /> : (
        <>
          {/* Cinq colonnes dont quatre champs numériques : sur mobile le tableau
              défile horizontalement au lieu d'écraser les libellés. */}
          <div className="-mx-1 overflow-x-auto px-1">
            <table className="w-full min-w-[440px] text-sm">
              <thead><tr className="text-xs text-slate-500 text-left"><th className="py-2">Commercial</th><th>Appels</th><th>Emails</th><th>RDV</th><th>Clients</th></tr></thead>
              <tbody>
                {commercials.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="py-2 pr-2 font-medium">{c.name}</td>
                    {["appels", "emails", "rdv", "clients"].map((k) => (
                      <td key={k} className="py-1 pr-2"><input type="number" min="0" data-testid={`obj-${c.id}-${k}`} value={rows[c.id]?.[k] ?? ""} onChange={(e) => setVal(c.id, k, Number(e.target.value))} className="w-20 px-2 py-1 rounded border border-slate-300 text-sm font-mono" /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button data-testid="save-objectives" onClick={saveAll} disabled={saving} className="w-full mt-4 py-2.5 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50">Enregistrer</button>
        </>
      )}
    </Modal>
  );
}
